import { useMemo, useState } from 'react';
import { D, type ByChatHistory, type HistoryPoint, type HistoryRange } from '@wave/shared';
import { EChart, isCoarsePointer, type EChartsOption } from '../EChart';
import { useIsNarrow } from '../../lib/useMediaQuery';
import { useBalanceHistory, useBalanceHistoryByChat } from './useBalanceHistory';
import { useUnmatched } from '../../api/hooks';
import { CHART_COLORS, EmptyHistory, chartGrid, chartLegend, tooltipFit, valueAxisLabel } from './ChartsPanel';
import { DivergenceModal } from './DivergenceModal';
import { topContributors } from './contributors';
import {
  bucketLabelToMinutes,
  confirmDivergences,
  detectDivergences,
  divergenceMatchWindow,
  fmtDeltaSuffix,
  fmtNum,
  type Divergence,
} from './trendMath';
import { robustAxisRange, robustWindowMs, rollingMedianByTime } from './robustMath';

/** Сводный тренд в USDT: Чистый (акцент) + Мы должны (красный) + Нам должны (зелёный, по модулю) + Кошельки (бирюзовый, пунктир) + Позиция (фиолетовый). */
export function TrendChart({ range, smooth }: { range: HistoryRange; smooth: boolean }) {
  const { data, isLoading } = useBalanceHistory(range);
  const narrow = useIsNarrow();
  const points = data?.points ?? [];
  // Бакет — из фактического ответа API (не карта по range: сервер — источник
  // истины про то, чем реально агрегирован конкретный ответ). На бакетах ≥4ч
  // (30д/90д/«Всё») detectDivergences сам себя выключает — см. trendMath.ts.
  const bucketMinutes = bucketLabelToMinutes(data?.bucket);

  // Кандидаты по графику — только грубый отбор (см. JSDoc thresholdAt в
  // trendMath.ts), окончательный вердикт даёт сверка с журналом ниже.
  const rawDivergences = useMemo(
    () => detectDivergences(points, { bucketMinutes }),
    [points, bucketMinutes],
  );
  // Окно сверки — ПО КАНДИДАТАМ (только `open`, единственный статус, который
  // confirmDivergences пересматривает), а не по всему графику: иначе на
  // 30д/90д/«Всё» окно тянет тысячи строк журнала и упирается в
  // предохранитель API (truncated), см. divergenceMatchWindow.
  const matchWindow = useMemo(
    () => divergenceMatchWindow(rawDivergences, points),
    [rawDivergences, points],
  );
  const { data: unmatched } = useUnmatched(matchWindow?.from ?? null, matchWindow?.to ?? null);
  // Пока ответ сверки не пришёл (или кандидатов нет вовсе) — показываем
  // сырые divergences как есть: НЕ понижаем `open` → `explained` раньше
  // времени, иначе маркер мигнёт «пропал-появился» при каждой загрузке.
  const divergences = useMemo(
    () =>
      unmatched ? confirmDivergences(rawDivergences, unmatched.items, unmatched.truncated) : rawDivergences,
    [rawDivergences, unmatched],
  );

  // По чатам — та же история, что у вкладки «Топ изменений» (кэш общий,
  // MoversChart уже держит её тёплой при том же range). Нужна только для
  // строки «Основной вклад» в тултипе точек с расхождением.
  const { data: byChat } = useBalanceHistoryByChat(range);

  // Окно операций для клика по пину — по КОНКРЕТНОЙ точке (index−1..index),
  // в отличие от matchWindow выше (тот — по всем кандидатам сразу, с
  // padding ±2ч, для сверки с журналом).
  const [divModal, setDivModal] = useState<{ from: string; to: string } | null>(null);

  const option = useMemo<EChartsOption>(() => {
    const ts = points.map((p) => p.ts);
    const tsMs = points.map((p) => new Date(p.ts).getTime());
    const raw = {
      net: points.map((p) => Number(p.netUsdt)),
      pos: points.map((p) => Number(p.posUsdt)),
      neg: points.map((p) => Math.abs(Number(p.negUsdt))),
      wallets: points.map((p) => Number(p.walletsUsdt)),
      position: positionSeries(points),
    };
    // Разброс позиции ВНУТРИ бакета (по всем снапшотам, см. history.ts) —
    // бледный коридор под линией: видно, где учёт «дрожал», не ломая уровень.
    const posMin = points.map((p) => Number(p.positionMinUsdt));
    const posMax = points.map((p) => Number(p.positionMaxUsdt));
    // Устойчивые линии: скользящая медиана по времени. Для позиции вход —
    // медиана бакета, а не его последний снапшот: перевод «в пути», пойманный
    // последним снапшотом часа, иначе растягивался на весь час.
    // Тултип всегда показывает сырые значения.
    const windowMs = robustWindowMs(range);
    const robust = (v: number[]) => rollingMedianByTime(tsMs, v, windowMs);
    const shown = smooth
      ? {
          net: robust(raw.net),
          pos: robust(raw.pos),
          neg: robust(raw.neg),
          wallets: robust(raw.wallets),
          position: robust(points.map((p) => Number(p.positionMedianUsdt))),
        }
      : raw;
    // Шкала — по нарисованным линиям без 2% крайних значений: один выброс до
    // ~1 млн больше не сжимает рабочий диапазон в полосу. Сырые точки позиции,
    // вылетевшие за шкалу, помечаются треугольником у края.
    const axis = robustAxisRange([shown.net, shown.pos, shown.neg, shown.wallets, shown.position]);
    const clipped = axis
      ? raw.position.flatMap((v, i) =>
          v > axis.max
            ? [{ coord: [i, axis.max], symbol: 'triangle', symbolSize: 8, symbolOffset: [0, 4], value: '' }]
            : v < axis.min
              ? [{ coord: [i, axis.min], symbol: 'triangle', symbolRotate: 180, symbolSize: 8, symbolOffset: [0, -4], value: '' }]
              : [],
        )
      : [];
    // Маркер на линии — только для подтверждённых статусов: 'pending' ещё не
    // дозрел (см. trendMath), 'explained' сверка с журналом сняла как ложную
    // тревогу — в обоих случаях маркер только пугал бы зря.
    const markerDivergences = divergences.filter((d) => d.status === 'closed' || d.status === 'open');
    // Тултип-пояснение — для всех, КРОМЕ 'explained' (снятая тревога — молчим
    // о ней вовсе, а не сообщаем нейтральным тоном, как для 'pending').
    const divByIndex = new Map(
      divergences.filter((d) => d.status !== 'explained').map((d) => [d.index, d]),
    );
    return {
      animation: false,
      grid: chartGrid(narrow),
      tooltip: {
        trigger: 'axis',
        ...tooltipFit(narrow),
        axisPointer: { type: 'cross' },
        formatter: (params: unknown) => {
          const arr = params as Array<{ dataIndex: number; seriesName: string; marker: string }>;
          const i = arr[0]?.dataIndex ?? 0;
          const key: Record<string, keyof typeof raw> = {
            Чистый: 'net',
            'Мы должны': 'pos',
            'Нам должны': 'neg',
            Кошельки: 'wallets',
            Позиция: 'position',
          };
          const rows = arr
            .map((s) => {
              const k = key[s.seriesName];
              if (!k) return '';
              const curr = raw[k][i]!;
              const suffix = fmtDeltaSuffix(curr, i > 0 ? raw[k][i - 1] : undefined);
              const fmt = (v: number) => v.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
              // Для позиции — ещё устойчивый уровень и разброс внутри бакета:
              // именно их сравнивает глаз, когда линия «прыгнула».
              const extra =
                k === 'position'
                  ? (smooth ? `<br/><span style="opacity:.75">&nbsp;&nbsp;устойчивая: <b>${fmt(shown.position[i]!)}</b></span>` : '') +
                    (posMax[i]! - posMin[i]! >= 1
                      ? `<br/><span style="opacity:.75">&nbsp;&nbsp;разброс в интервале: ${fmt(posMin[i]!)} … ${fmt(posMax[i]!)}</span>`
                      : '')
                  : '';
              return `${s.marker} ${s.seriesName}: <b>${curr.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}</b>${suffix}${extra}`;
            })
            .filter(Boolean)
            .join('<br/>');
          const d = divByIndex.get(i);
          const note = d
            ? `<br/><span style="color:${noteColor(d.status)}">⚠ Операция в пути: чаты ${fmtNum(d.chatsDelta)}, кошельки ${fmtNum(d.walletsDelta)}${noteClause(d, range)}</span>`
            : '';
          // Топ-3 чата-вкладчика — только для 'open'/'closed' (уже
          // подтверждённые графиком расхождения): для 'pending'/'explained'
          // (и точек без расхождения) строка не нужна, не засоряем тултип.
          const contribNote =
            d && (d.status === 'open' || d.status === 'closed')
              ? contributorsLine(byChat, points, i)
              : '';
          const smoothNote = smooth
            ? `<br/><span style="opacity:.6">линии — устойчивый уровень (медиана за ${Math.round(windowMs / 3600_000)} ч), значения — сырые</span>`
            : '';
          // На тач-экране тап показывает тултип, действие — повторный тап (см. EChart onGridClick).
          const clickNote = `<br/><span style="opacity:.6">${isCoarsePointer() ? 'повторный тап' : 'клик'} — операции за интервал</span>`;
          return `${new Date(points[i]!.ts).toLocaleString('ru-RU')}<br/>${rows}${note}${contribNote}${smoothNote}${clickNote}`;
        },
      },
      legend: {
        ...chartLegend(narrow),
        data: ['Чистый', 'Мы должны', 'Нам должны', 'Кошельки', 'Позиция', 'Разброс позиции'],
      },
      xAxis: { type: 'category', data: ts, axisLabel: { color: CHART_COLORS.text, formatter: (v: string) => fmtTs(v, range), ...(narrow ? { fontSize: 10, hideOverlap: true } : {}) } },
      yAxis: {
        type: 'value',
        ...(axis ?? {}),
        axisLabel: valueAxisLabel(narrow),
        splitLine: { lineStyle: { color: CHART_COLORS.grid } },
      },
      dataZoom: [{ type: 'inside' }, { type: 'slider', height: 18, bottom: 8 }],
      series: [
        // Коридор min…max позиции: невидимая нижняя граница + стек ширины.
        {
          name: '__posBandBase',
          type: 'line',
          data: posMin,
          stack: 'posBand',
          showSymbol: false,
          silent: true,
          lineStyle: { opacity: 0 },
          tooltip: { show: false },
        },
        {
          name: 'Разброс позиции',
          type: 'line',
          data: posMax.map((v, i) => v - posMin[i]!),
          stack: 'posBand',
          showSymbol: false,
          silent: true,
          lineStyle: { opacity: 0 },
          areaStyle: { color: '#7c3aed', opacity: 0.1 },
          itemStyle: { color: '#c4b5fd' },
        },
        { name: 'Чистый', type: 'line', data: shown.net, showSymbol: false, lineStyle: { width: 2.5, color: CHART_COLORS.net }, itemStyle: { color: CHART_COLORS.net } },
        { name: 'Мы должны', type: 'line', data: shown.pos, showSymbol: false, lineStyle: { width: 1.5, color: CHART_COLORS.pos }, itemStyle: { color: CHART_COLORS.pos } },
        { name: 'Нам должны', type: 'line', data: shown.neg, showSymbol: false, lineStyle: { width: 1.5, color: CHART_COLORS.neg }, itemStyle: { color: CHART_COLORS.neg } },
        {
          name: 'Кошельки',
          type: 'line',
          data: shown.wallets,
          showSymbol: false,
          lineStyle: { width: 1.5, type: 'dashed', color: '#0891b2' },
          itemStyle: { color: '#0891b2' },
        },
        {
          name: 'Позиция',
          type: 'line',
          data: shown.position,
          showSymbol: false,
          lineStyle: { width: 2, color: '#7c3aed' },
          itemStyle: { color: '#7c3aed' },
          markPoint: {
            symbol: 'pin',
            symbolSize: 34,
            data: [
              ...markerDivergences.map((d) => ({
                coord: [d.index, shown.position[d.index]!],
                itemStyle: { color: d.status === 'open' ? '#dc2626' : '#f59e0b' },
                value: d.status === 'open' ? '!' : '~',
              })),
              ...clipped.map((c) => ({ ...c, itemStyle: { color: '#7c3aed', opacity: 0.55 } })),
            ],
          },
        },
      ],
    };
  }, [points, range, smooth, divergences, byChat, narrow]);


  if (isLoading) return <div className="h-40 animate-pulse rounded bg-bg3" />;
  if (points.length < 2) return <EmptyHistory pointsCount={points.length} />;
  // Баннер «расхождение открыто» — на уровне страницы (BalancesPage), а не
  // здесь: панель графиков по умолчанию свёрнута, внутри неё алерт о
  // потенциальной потере денег был бы фактически невидим (I3 финального
  // ревью). TrendChart отвечает только за сам график.
  return (
    <>
      <EChart
        option={option}
        height={340}
        // Модалка «что произошло» открывается кликом по ЛЮБОМУ месту графика:
        // индекс берётся у ближайшей по X точки (см. onGridClick в EChart —
        // клик по самой линии недостижим, символы скрыты).
        onGridClick={(idx) => {
          if (idx < 0 || idx >= points.length) return;
          // Окно = интервал между соседними точками. У самой первой точки
          // предыдущей нет — берём бакет назад (истории до неё нет, но
          // операции за этот интервал в журнале есть).
          const to = points[idx]!.ts;
          const from =
            idx > 0
              ? points[idx - 1]!.ts
              : new Date(new Date(to).getTime() - (bucketMinutes ?? 15) * 60_000).toISOString();
          setDivModal({ from, to });
        }}
      />
      {divModal && (
        <DivergenceModal from={divModal.from} to={divModal.to} onClose={() => setDivModal(null)} />
      )}
    </>
  );
}

/**
 * Серия «Позиция» = кошельки − чистый остаток чатов, по каждой точке истории.
 * Арифметика на Decimal (строки из API), Number — только на границе рендера.
 */
export function positionSeries(points: Pick<HistoryPoint, 'walletsUsdt' | 'netUsdt'>[]): number[] {
  return points.map((p) => D(p.walletsUsdt).minus(D(p.netUsdt)).toNumber());
}

/** Подпись оси: для 24ч/7д — время, дальше — дата (локальное время браузера). */
export function fmtTs(iso: string, range: HistoryRange): string {
  const d = new Date(iso);
  if (range === '24h') return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (range === '7d')
    return d.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

/**
 * Строка «Основной вклад: …» для тултипа точки с расхождением — топ-3 чата
 * по |Δ net USDT| между моментами `points[index-1].ts` и `points[index].ts`
 * (см. `topContributors` — сопоставление по `ts`, не по позиции в ряде
 * чата). Пусто, если история по чатам ещё не загружена, нет предыдущей
 * точки (`index <= 0`) или вклада нет вовсе (например, расхождение целиком
 * за счёт кошельков).
 */
function contributorsLine(
  byChat: ByChatHistory | undefined,
  points: HistoryPoint[],
  index: number,
): string {
  if (!byChat || index <= 0) return '';
  const top = topContributors(byChat, points[index - 1]!.ts, points[index]!.ts);
  if (top.length === 0) return '';
  const text = top.map((c) => `${c.title} ${fmtNum(c.deltaUsdt)}`).join(', ');
  return `<br/><span style="opacity:.75">Основной вклад: ${text}</span>`;
}

/** Цвет пояснения расхождения в тултипе: open — тревога, closed — сгладилось, pending — нейтрально (ещё не вывод). */
function noteColor(status: Divergence['status']): string {
  if (status === 'open') return '#dc2626';
  if (status === 'closed') return '#b45309';
  return '#64748b';
}

/**
 * Хвост пояснения расхождения после «кошельки N»: для closed — время (и дата
 * на диапазонах ≥7д, иначе голое время вводит в заблуждение) второй ноги;
 * для open — не проведена, плюс подтверждение сверкой (см. evidenceClause);
 * для pending — осторожная формулировка (кандидат ещё может закрыться, «не
 * проведена» было бы преждевременным выводом).
 */
function noteClause(d: Divergence, range: HistoryRange): string {
  if (d.status === 'closed' && d.closedAtTs) return `; вторая нога — ${fmtClosedAt(d.closedAtTs, range)}`;
  if (d.status === 'open') return `; вторая нога не проведена${evidenceClause(d)}`;
  return '; возможно, вторая нога ещё в пути';
}

/**
 * Подтверждение из сверки с журналом (только для `open`): если сверка нашла
 * непарную строку — её реквизиты (это не голословный алерт, а конкретная
 * строка журнала без пары); если сверка была неполной (`truncated` в ответе
 * API — см. `matchIncomplete` в trendMath.ts) — явная оговорка, чтобы не
 * читалось как «проверено и ничего не нашли», когда на деле проверили не всё
 * окно.
 */
function evidenceClause(d: Divergence): string {
  if (d.evidence) {
    const actor = d.evidence.actor ?? '—';
    return `; не найдена пара: ${d.evidence.title}, ${fmtNum(Number(d.evidence.delta))} ${d.evidence.ccy}, ${actor}`;
  }
  if (d.matchIncomplete) return '; сверка по журналу неполная — окно велико, пара могла не попасть в выборку';
  return '';
}

/** Момент второй ноги в пояснении: 24ч — только время, иначе + дата (дд.мм), иначе время вводит в заблуждение. */
function fmtClosedAt(iso: string, range: HistoryRange): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (range === '24h') return time;
  return `${d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })} ${time}`;
}
